import PropTypes from "prop-types";
import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Grid, Button, Typography, Slider } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { GeofencesContext } from "../../context";
import {
  isPointPolygon,
  getKRing,
  fillWithHexes,
  compactHexes,
  fillWithParentHexes,
} from "../../services/geofences.service";
import { useDialog } from "../../hooks/useDialog";
import { filterHexesOutsidePolygon } from "../../util/helpers";

import {
  GeofencesEditModes,
  GeofencesThresholds,
  GeofencesResolutionRange,
  ErrorVisibilityDuration,
} from "../../constants";

const useStyles = makeStyles((theme) => ({
  optionsContainer: {
    width: "unset",
    flexGrow: 1,
  },
  sliderWrapper: {
    width: "8em",
    marginRight: theme.spacing(3),
    paddingTop: theme.spacing(0.5),
  },
  slider: {
    "& > span": { opacity: 1 },
  },
  generateButton: {
    marginLeft: "auto",
  },
  warning: {
    position: "absolute",
    zIndex: 999,
    bottom: -theme.spacing(2),
    left: 0,
    right: 0,
  },
}));

const HexFillOptions = ({ style, sidewalksMode }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();

  const {
    layers,
    selectedLayer,
    selectedZone,
    updateZone,
    updateSelectedZone,
    updateSelectedLayer,
    loadScheduledRules,
    setSidewalksEditMode,
  } = useContext(GeofencesContext);

  const [openPolyfillDialog, polyfillDialog] = useDialog({
    title: t("Fleet.Geofences.Dialogs.PolyfillTitle"),
    body: t("Fleet.Geofences.Dialogs.PolyfillBody"),
  });

  const [openPolygonDeleteDialog, polygonDeleteDialog] = useDialog({
    title: t("Fleet.Geofences.Dialogs.PolygonTitle"),
    body: t("Fleet.Geofences.Dialogs.PolygonBody"),
  });

  const [emptyHexesWarning, setEmptyHexesWarning] = useState(false);

  const { editMode, resolution } = selectedLayer.key ? selectedLayer : selectedZone;

  const layer = layers[selectedZone.parent || selectedLayer.key];
  const zone = selectedZone.key ? layer.zones[selectedZone.key] : null;

  useEffect(() => {
    if (emptyHexesWarning) {
      setTimeout(() => {
        setEmptyHexesWarning(false);
      }, ErrorVisibilityDuration);
    }
    if (zone?.rules) loadScheduledRules(zone);
  }, [zone?.rules, emptyHexesWarning]);

  const onResolutionChange = (e, v) => {
    if (selectedZone.key) updateSelectedZone({ resolution: v });
    else if (selectedLayer.key) updateSelectedLayer({ resolution: v });
  };

  const getCompactedHexes = (largeHexes, zoneToFillPolygon) => {
    let compactedHexes;
    if (sidewalksMode) {
      const shrinkedHexes = compactHexes(largeHexes);
      compactedHexes = filterHexesOutsidePolygon(shrinkedHexes, zoneToFillPolygon[0]);
    } else {
      compactedHexes = compactHexes(largeHexes);
    }
    return compactedHexes;
  };

  const fillZone = (zoneToFill) => {
    if (isPointPolygon(zoneToFill.polygon)) {
      const hexes = getKRing({ resolution, polygon: zoneToFill.polygon });

      if (selectedZone.key) {
        updateZone(selectedZone.key, selectedZone.parent, { hexes });
        updateSelectedZone({ editMode: GeofencesEditModes.hex });
      } else {
        updateZone(zoneToFill.key, selectedLayer.key, { hexes });
      }

      if (sidewalksMode) {
        setSidewalksEditMode(GeofencesEditModes.hex);
      }

      return;
    }

    const isMaxResolution = resolution === 15; // magic number: max resolution that h3 supports
    const hexResolution = isMaxResolution ? resolution : resolution + 1;

    const hexes = fillWithHexes({ resolution: hexResolution, polygon: zoneToFill.polygon });

    const expandedHexes = isMaxResolution
      ? hexes
      : fillWithParentHexes({
          resolution: hexResolution,
          childrenThreshold: GeofencesThresholds[layer.type],
          hexes,
        });
    const compactedHexes = getCompactedHexes(expandedHexes, zoneToFill.polygon);

    if (!Object.keys(compactedHexes).length) {
      setEmptyHexesWarning(true);
    }

    if (selectedZone.key) {
      updateZone(selectedZone.key, selectedZone.parent, { hexes: compactedHexes });
      updateSelectedZone({ editMode: GeofencesEditModes.hex });
    } else {
      updateZone(zoneToFill.key, selectedLayer.key, { hexes: compactedHexes });
    }

    if (sidewalksMode) {
      setSidewalksEditMode(GeofencesEditModes.hex);
    }
  };

  const onPolyfillClick = () => {
    const callback = () => {
      if (zone) {
        fillZone(zone);
      } else {
        Object.values(layer.zones).forEach((zn) => fillZone(zn));
      }
    };

    const zoneHasHexes = zone
      ? Object.keys(zone.hexes).length > 0
      : Object.values(layer.zones).some((zn) => Object.keys(zn.hexes).length > 0);

    if (zoneHasHexes) {
      openPolyfillDialog(() => callback);
    } else {
      callback();
    }
  };

  const onDeletePolygon = () => {
    openPolygonDeleteDialog(() => () => {
      updateZone(selectedZone.key, selectedZone.parent, { polygon: null });
    });
  };

  return (
    <Grid item container alignItems="center" className={classes.optionsContainer} style={style}>
      <div className={classes.sliderWrapper}>
        <Typography variant="caption">
          {`${t("Fleet.Geofences.ZoneResolutionLabel")}: ${resolution}`}
        </Typography>
        <Slider
          key={`${(zone || layer).key}-resolution`}
          defaultValue={
            sidewalksMode
              ? GeofencesResolutionRange.defaultSidewalks
              : GeofencesResolutionRange.default
          }
          min={GeofencesResolutionRange.min}
          max={sidewalksMode ? GeofencesResolutionRange.maxSidewalks : GeofencesResolutionRange.max}
          step={1}
          marks
          valueLabelDisplay="auto"
          onChangeCommitted={onResolutionChange}
          className={classes.slider}
        />
      </div>
      <div className={classes.generateButton}>
        {!!zone?.polygon && (
          <Button onClick={onDeletePolygon}>{t("Fleet.Geofences.Buttons.PolygonDelete")}</Button>
        )}
        <Button
          onClick={onPolyfillClick}
          disabled={[GeofencesEditModes.hex, GeofencesEditModes.hexBrush].includes(editMode)}
        >
          {t("Fleet.Geofences.Buttons.Polyfill")}
        </Button>
      </div>

      {polyfillDialog}
      {polygonDeleteDialog}

      {emptyHexesWarning && (
        <Alert severity="info" className={classes.warning}>
          {t("Fleet.Geofences.EmptyHexesWarning")}
        </Alert>
      )}
    </Grid>
  );
};

HexFillOptions.propTypes = {
  style: PropTypes.object,
  sidewalksMode: PropTypes.bool,
};

HexFillOptions.defaultProps = {
  style: {},
  sidewalksMode: false,
};

export default HexFillOptions;
